import { Box, debounce, IconButton, InputAdornment, Typography } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import { colorTheme } from '@styles/muiTheme';
import {
	selectLastCommentsRefresh,
	selectLastCommunicationDate,
	selectLenderCode,
	selectPayoffQuoteResponse,
	selectWorkflowNextStep,
} from '@store/reducers/contactManagerSlice';
import { withQueryParams } from '@shared/commonFunctions';
import { SERVICES } from '@shared/constants/apiConfig';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { TextInput } from '@shared/inputs/TextInput';
import { SaveButton } from '@shared/inputs/Buttons';
import {
	AddCommentBlock,
	CommentBox,
	FilterFieldWrapper,
	SearchBlock
} from '@styles/ContactManager/CommentsTabStyles';

import { sortArray } from '@shared/utils/helpers';
import { Comment } from './Comment';
import { requestCreateContactComment, requestCreateDealComment } from './queries';
import { COMMENT_TYPES, IComment, IContactCommentsResponse } from './types';

const filterOptions = [
	{ label: 'Users', value: COMMENT_TYPES.Comment },
	{ label: 'Activity', value: COMMENT_TYPES.Activity },
	{ label: 'Workflow', value: COMMENT_TYPES.Workflow }
];

type CommentsProps = {
	isDeal?: boolean;
	dealId?: string;
	contactId: string;
};

export const Comments = ({ isDeal, dealId = '', contactId = '', }: CommentsProps) => {
	const [commentTypes, setCommentTypes] = useState<COMMENT_TYPES[]>([]);

	const contactPath = withQueryParams(`/${SERVICES.CONTACT}/contact-comments`, {
		commentTypes,
		contactId,
		pageNumber: 1,
		pageSize: 1000,
	}, false);

	const dealPath = withQueryParams(`/${SERVICES.DEAL}/deals/${dealId}/comments`, {
		commentTypes,
		pageNumber: 1,
		pageSize: 1000,
	}, false);

	const { data, mutate } = useSWR<IContactCommentsResponse>(isDeal ? dealPath : contactPath, {
		revalidateOnMount: false,
		revalidateIfStale: true,
	});

	const [comment, setComment] = useState('');
	const [filter, setFilter] = useState('');
	const [loading, setLoading] = useState(false);
	const [filteredComments, setFilteredComments] = useState<IComment[]>(data?.items ?? []);

	const lenderCode = useSelector(selectLenderCode);
	const workflowDetailsNextStep = useSelector(selectWorkflowNextStep);
	const lastCommunicationDate = useSelector(selectLastCommunicationDate);
	const payOffQuoteResponse = useSelector(selectPayoffQuoteResponse);
	const lastCommentsRefresh = useSelector(selectLastCommentsRefresh);

	useEffect(() => {
		mutate();
	}, [lenderCode, workflowDetailsNextStep, lastCommunicationDate, payOffQuoteResponse, lastCommentsRefresh]);

	useEffect(() => {
		setFilteredComments(data?.items ?? []);
		modifyData(data?.items, filter);
	}, [data?.items, filter]);

	const modifyData = debounce(useCallback((items: IComment[] = [], searchStr: string) => {
		let comments: IComment[] = items;
		if (searchStr.length > 0) {
			const filteredData = items?.filter((item) => item && item.text?.toLowerCase().includes(searchStr.toLowerCase())) ?? [];
			comments = filteredData.map(item => {
				const highlightedText = item.text?.replace(new RegExp(`(${searchStr})`, 'gi'),
					match => `<mark style="background: ${colorTheme.palette.primary.light}; color: ${colorTheme.palette.common.white}">${match}</mark>`
				);
				return { ...item, highlightedText };
			});
		}
		setFilteredComments(comments);
	}, []), 300);

	const onAddComment = useCallback(async () => {
		setLoading(true);
		isDeal
			? await requestCreateDealComment(dealId, {
				comment: comment,
				commentActionType: COMMENT_TYPES.UserComment,
			})
			: await requestCreateContactComment({
				commentType: COMMENT_TYPES.UserComment,
				text: comment,
				contactId,
			});
		mutate();
		setComment('');
		setLoading(false);
	}, [requestCreateDealComment, requestCreateContactComment, comment, contactId, dealId, isDeal]);

	const handleChangeFilterOptions = useCallback((value: COMMENT_TYPES[]) => setCommentTypes([...value]), [commentTypes]);

	const handleSearch = useCallback((searchStr: string) => {
		setFilter(searchStr);
	}, [data?.items]);

	return (
		<>
			<CommentBox
				multiline
				rows={4}
				value={comment}
				placeholder="Type your comment here"
				onChange={({ target: { value } }) => setComment(value)}
			/>

			<AddCommentBlock>
				<SaveButton
					fullWidth
					label={!loading ? 'Add Comment' : 'Adding Comment...'}
					disabled={!comment || loading}
					onClick={debounce(onAddComment, 300)}
				/>
			</AddCommentBlock>

			<SearchBlock>
				<Box width="100%">
					<TextInput
						placeholder="Search"
						value={filter}
						InputProps={{
							endAdornment: (
								filter && <InputAdornment position="end">
									<IconButton onClick={() => handleSearch('')}>
										<Clear />
									</IconButton>
								</InputAdornment>
							)
						}}
						onChange={(event) => handleSearch(event.target.value)}
					/>
				</Box>
				<FilterFieldWrapper>
					<Typography fontWeight={700}>Filter:</Typography>
					<SelectAutocomplete
						multiple
						removeExtraPadding
						value={commentTypes}
						placeholder="Filter Comments..."
						options={filterOptions}
						onChange={handleChangeFilterOptions}
					/>
				</FilterFieldWrapper>
			</SearchBlock>

			{sortArray(filteredComments, 'createdDate', 'desc')?.map((item) => <Comment key={item.id} data={item} filter={filter} />)}
		</>
	);
};
